import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, CheckCircle, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface ContactMessage {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  subject: string;
  message: string;
  service_type: string;
  created_at: string;
  read_at: string | null;
}

interface MessageCardProps {
  message: ContactMessage;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  formatDate: (date: string) => string;
}

const MessageCard = ({ message, onMarkAsRead, onDelete, formatDate }: MessageCardProps) => {
  return (
    <Card className={`glass p-6 border-glow ${!message.read_at ? 'ring-2 ring-cyber-blue/20' : ''}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">
              {message.first_name} {message.last_name}
            </h3>
            {!message.read_at && (
              <Badge variant="default" className="bg-cyber-blue text-primary-foreground">
                New
              </Badge>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="w-4 h-4" />
          {formatDate(message.created_at)}
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Email:</span>
            <p className="font-medium">{message.email}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Subject:</span>
            <p className="font-medium">{message.subject}</p>
          </div>
          {message.service_type && (
            <div>
              <span className="text-muted-foreground">Service Type:</span>
              <p className="font-medium">{message.service_type}</p>
            </div>
          )}
        </div>
        
        <div>
          <span className="text-muted-foreground text-sm">Message:</span>
          <p className="mt-1 p-3 bg-muted/30 rounded-lg whitespace-pre-wrap">
            {message.message}
          </p>
        </div>
        
        <div className="flex justify-end gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="glass border-destructive text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="glass border-glow">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Message</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this message from {message.first_name} {message.last_name}? 
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="glass">Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={() => onDelete(message.id)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {!message.read_at && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onMarkAsRead(message.id)}
              className="glass border-cyber-green text-cyber-green hover:bg-cyber-green/10"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark as Read
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default MessageCard;
